import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Mail,
  Phone,
  Loader2,
  Tag,
  Plus,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MerchantDetailsCard } from "@/components/merchant/MerchantDetailsCard";
import { AddOfferForOfferPageDialog } from "@/components/merchant/AddOfferForOfferPageDialog";
import {
  EditOfferForOfferPageDialog,
  type OfferForEditInitial,
} from "@/components/merchant/EditOfferForOfferPageDialog";
import { EditOfferMerchantSheet } from "@/components/merchants/EditOfferMerchantSheet";
import { IMerchant } from "@/interfaces/merchant.interfaces";
import { MerchantApi } from "@/api";
import type { MerchantOfferAddressDetailVO } from "@/api/merchant.api";
import { toast } from "sonner";
import { getFileUrl } from "@/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BASE_PATH = "/offers";

function getContactPhoneList(contact: Record<string, unknown>) {
  return (
    (contact?.merchantContactPhoneVOList as unknown[]) ||
    (contact?.merchantOfOfferContactPhoneVOList as unknown[]) ||
    []
  );
}

function formatOfferMillis(ms?: number | string | null) {
  if (ms == null || ms === "") return "—";
  const n = typeof ms === "string" ? Number(ms) : ms;
  if (!Number.isFinite(n)) return "—";
  try {
    return format(new Date(n), "MMM d, yyyy");
  } catch {
    return "—";
  }
}

/** Treat API null / literal "null" / blank as no URL for display and edit. */
function normalizeOfferUrlForDisplay(
  value: string | null | undefined
): string {
  if (value == null) return "";
  const trimmed = String(value).trim();
  if (!trimmed || trimmed.toLowerCase() === "null") return "";
  return trimmed;
}

function normalizeOffersResponse(data: unknown): any[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const candidates = [
      o.merchantOfferVOList,
      o.offerVOList,
      o.offers,
      o.content,
      o.data,
    ];
    for (const c of candidates) {
      if (Array.isArray(c)) return c;
    }
  }
  return [];
}

function asMerchantOfOfferRecord(
  vo: unknown
): Record<string, unknown> | null {
  if (vo && typeof vo === "object") return vo as Record<string, unknown>;
  return null;
}

function categorySummaryFromMerchantOfOffer(
  merchantVo: Record<string, unknown> | null
): string {
  const list = merchantVo?.categoryVOList as
    | Array<{ categoryName?: string; categoryCode?: string }>
    | undefined;
  if (!Array.isArray(list) || list.length === 0) return "";
  const labels = list
    .map((c) => c.categoryName || c.categoryCode)
    .filter(Boolean) as string[];
  if (labels.length === 0) return "";
  if (labels.length <= 2) return labels.join(", ");
  return `${labels[0]}, +${labels.length - 1} more`;
}

function deriveOfferTitle(raw: Record<string, unknown>): string {
  const explicit =
    (raw.title as string) ||
    (raw.name as string) ||
    (raw.offerTitle as string);
  if (explicit?.trim()) return explicit.trim();

  const desc = (raw.description as string)?.trim();
  if (desc) {
    const max = 72;
    return desc.length > max ? `${desc.slice(0, max)}…` : desc;
  }

  const id = raw.id ?? raw.offerId;
  return id != null ? `Offer #${id}` : "Offer";
}

function parseOfferAddressDetailsFromRaw(
  raw: Record<string, unknown>
): MerchantOfferAddressDetailVO[] | undefined {
  /** `getOffersOfMerchantFromDB` returns each offer with `addressDetailsVOList` */
  const rawList =
    raw.addressDetailsVOList ??
    raw.merchantOfferAddressDetailsVOList ??
    raw.offerAddressDetailsVOList;
  if (!Array.isArray(rawList) || rawList.length === 0) return undefined;
  const out: MerchantOfferAddressDetailVO[] = [];
  for (const item of rawList) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const idRaw = row.id;
    const idNum =
      idRaw != null && idRaw !== ""
        ? Number(idRaw)
        : Number.NaN;
    const vo: MerchantOfferAddressDetailVO = {
      address: String(row.address ?? ""),
      city: String(row.city ?? ""),
      country: String(row.country ?? ""),
      location: String(row.location ?? ""),
      phoneCountryCode: String(row.phoneCountryCode ?? ""),
      phoneNumber: String(row.phoneNumber ?? ""),
    };
    if (Number.isFinite(idNum)) {
      vo.id = idNum;
    }
    out.push(vo);
  }
  return out.length ? out : undefined;
}

function mapOfferForDisplay(raw: Record<string, unknown>) {
  const merchantVo = asMerchantOfOfferRecord(raw.merchantOfOfferVO);

  const uri =
    (raw.imageUri as string) ||
    (raw.offerImageUri as string) ||
    (raw.bannerImageUri as string) ||
    "";
  const img = uri
    ? uri.startsWith("http")
      ? uri
      : getFileUrl(uri) || "/placeholder.svg"
    : "/placeholder.svg";

  const categoryVO = raw.categoryVO as
    | { id?: number; categoryName?: string }
    | undefined;
  const categoryFromOffer =
    categoryVO?.categoryName ||
    (raw.categoryName as string) ||
    "";

  const categoryIdFromApi =
    raw.categoryId != null && raw.categoryId !== ""
      ? String(raw.categoryId)
      : categoryVO?.id != null
        ? String(categoryVO.id)
        : "";

  const category =
    categoryFromOffer || categorySummaryFromMerchantOfOffer(merchantVo);

  const active = raw.isActive;
  const status =
    active === false ? "Inactive" : active === true ? "Active" : "Active";

  const termsRaw =
    (raw.termsAndConditions as string) ||
    (raw.termsandConditions as string) ||
    "";

  const addressDetailsVOList = parseOfferAddressDetailsFromRaw(raw);

  return {
    id: String(raw.id ?? raw.offerId ?? ""),
    title: deriveOfferTitle(raw),
    description: (raw.description as string) || "",
    category: category || "—",
    categoryId: categoryIdFromApi,
    startDateInMillis: raw.startDateInMillis as number | string | undefined,
    endDateInMillis: raw.endDateInMillis as number | string | undefined,
    status,
    imageSrc: img,
    offerURL: normalizeOfferUrlForDisplay(
      (raw.offerURL as string) ?? (raw.offerUrl as string)
    ),
    termsAndConditions: termsRaw,
    addressDetailsVOList,
  };
}

const OfferMerchantDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const routeMerchant = location.state?.merchant as IMerchant | undefined;

  const [merchant, setMerchant] = useState<IMerchant | undefined>(undefined);
  const [editSheetOpen, setEditSheetOpen] = useState(false);

  useEffect(() => {
    setMerchant(routeMerchant ?? undefined);
  }, [routeMerchant]);

  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [addOfferOpen, setAddOfferOpen] = useState(false);
  const [editOfferDraft, setEditOfferDraft] =
    useState<OfferForEditInitial | null>(null);
  const [activeTab, setActiveTab] = useState("details");
  const [offers, setOffers] = useState<ReturnType<typeof mapOfferForDisplay>[]>(
    []
  );
  const [offersLoading, setOffersLoading] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [deleteOfferConfirmOpen, setDeleteOfferConfirmOpen] = useState(false);
  const [deletingOffer, setDeletingOffer] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setContactsLoading(true);
      try {
        const response = await MerchantApi.getMerchantContacts(id);
        if (!cancelled) {
          setContacts(response?.data || []);
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load contacts");
          setContacts([]);
        }
      } finally {
        if (!cancelled) setContactsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const fetchOffers = useCallback(async () => {
    if (!id) return;
    setOffersLoading(true);
    try {
      const response = await MerchantApi.getOffersOfMerchantFromDB({
        startDateInMillis: "",
        endDateInMillis: "",
        merchantId: String(id),
        cashbackPercentage: "",
        sortOrder: "desc",
      });
      const list = normalizeOffersResponse(response?.data);
      setOffers(
        list.map((item) =>
          mapOfferForDisplay(item as Record<string, unknown>)
        )
      );
    } catch {
      toast.error("Failed to load offers");
      setOffers([]);
    } finally {
      setOffersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab !== "offers" || !id) return;
    fetchOffers();
  }, [activeTab, id, fetchOffers]);

  const handleDeleteOfferClick = (offer: { id: string; title: string }) => {
    setOfferToDelete({ id: offer.id, title: offer.title });
    setDeleteOfferConfirmOpen(true);
  };

  const confirmDeleteOffer = async () => {
    if (!offerToDelete) return;
    setDeletingOffer(true);
    try {
      await MerchantApi.deleteOffersOfMerchant(offerToDelete.id);
      if (editOfferDraft?.id === offerToDelete.id) {
        setEditOfferDraft(null);
      }
      await fetchOffers();
      toast.success("Offer deleted successfully");
      setDeleteOfferConfirmOpen(false);
      setOfferToDelete(null);
    } catch {
      toast.error("Failed to delete offer");
    } finally {
      setDeletingOffer(false);
    }
  };

  if (!routeMerchant) {
    return (
      <div className="p-8 text-center text-red-600 font-medium">
        No merchant data found. Open this page from the Offer Point merchants list.
      </div>
    );
  }

  const displayMerchant = (merchant ??
    routeMerchant) as IMerchant & Record<string, unknown>;
  const categoryVOList = (displayMerchant as any)?.categoryVOList as
    | Array<{
        id?: number;
        categoryName?: string;
        categoryCode?: string;
        cashbackPercentage?: number;
      }>
    | undefined;

  const offerCategoryOptions = useMemo(() => {
    if (!Array.isArray(categoryVOList)) return [];
    return categoryVOList
      .filter((c) => c?.id != null)
      .map((c) => ({
        id: Number(c.id),
        categoryName: c.categoryName,
        categoryCode: c.categoryCode,
      }));
  }, [categoryVOList]);

  const tabTriggerClass =
    "data-[state=active]:border-b-2 data-[state=active]:border-rose-600 rounded-none data-[state=active]:text-rose-700 data-[state=active]:shadow-none";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-2">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild className="bg-white">
            <Link to={`${BASE_PATH}/merchants`}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Link>
          </Button>
          <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 mb-1">
            Offer Point
          </p>
            <h1 className="text-2xl font-bold text-gray-800">Merchant Details</h1>
            <p className="text-gray-500 text-sm">
              View merchant information, contacts, and offers
            </p>
          </div>
        </div>
        <Button
          type="button"
          onClick={() => setEditSheetOpen(true)}
          className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700 shrink-0"
        >
          <Edit className="h-4 w-4" /> Edit Merchant
        </Button>
      </div>

      <MerchantDetailsCard merchant={displayMerchant} />

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
          <TabsTrigger value="details" className={tabTriggerClass}>
            Merchant Details
          </TabsTrigger>
          <TabsTrigger value="contacts" className={tabTriggerClass}>
            Contacts
          </TabsTrigger>
          <TabsTrigger value="offers" className={tabTriggerClass}>
            Offers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Merchant Name
                  </p>
                  <p className="text-gray-800">{displayMerchant?.merchantName || "—"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Merchant Code
                  </p>
                  <p className="text-gray-800">{displayMerchant?.merchantCode || "—"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Description</p>
                  <p className="text-gray-800">
                    {displayMerchant?.businessDescription || "No description provided"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Website URL</p>
                  <p className="text-gray-800">
                    {displayMerchant?.websiteUrl ? (
                      <a
                        href={displayMerchant.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-rose-600 hover:text-rose-800 underline"
                      >
                        {displayMerchant.websiteUrl}
                      </a>
                    ) : (
                      "No website provided"
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Location
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Country</p>
                    <p className="text-gray-800">{displayMerchant?.country || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">City</p>
                    <p className="text-gray-800">{displayMerchant?.city || "—"}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Business Location
                  </p>
                  <p className="text-gray-800">
                    {displayMerchant?.businessLocation || "—"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <Tag className="h-5 w-5 text-gray-600" />
                Categories
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {categoryVOList?.length ? (
                <div className="space-y-3">
                  {categoryVOList.map((category, index) => (
                    <div
                      key={
                        category.id != null
                          ? String(category.id)
                          : `${category.categoryCode || category.categoryName}-${index}`
                      }
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 rounded-lg border border-gray-100 bg-gray-50/50"
                    >
                      <div>
                        <p className="font-medium text-gray-800">
                          {category.categoryName || "—"}
                        </p>
                        {category.categoryCode && (
                          <p className="text-sm text-gray-500">
                            Code: {category.categoryCode}
                          </p>
                        )}
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-sm font-medium text-gray-500">
                          Cashback
                        </p>
                        <p className="text-lg font-semibold text-rose-600">
                          {category.cashbackPercentage != null
                            ? `${category.cashbackPercentage}%`
                            : "—"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-gray-500">
                  No categories for this merchant.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-6 pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Merchant Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {contactsLoading ? (
                <div className="flex items-center justify-center py-12 text-gray-500 gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Loading contacts…
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <p className="font-medium text-gray-700 mb-1">No contacts found</p>
                  <p className="text-sm">
                    There are no contacts on file for this merchant yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {contacts.map((contact) => {
                    const phones = getContactPhoneList(contact);
                    const primary = phones[0] as
                      | { phoneCountryCode?: string; phoneNumber?: string }
                      | undefined;
                    const phoneDisplay = primary
                      ? [primary.phoneCountryCode, primary.phoneNumber]
                          .filter(Boolean)
                          .join(" ")
                      : null;
                    return (
                      <div
                        key={contact?.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-gray-100 last:border-0 last:pb-0 gap-3"
                      >
                        <div>
                          <p className="font-medium text-gray-800">
                            {contact?.contactName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {contact?.contactRole}
                          </p>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-gray-600">
                          <div className="flex items-center gap-1 text-sm">
                            <Mail className="h-4 w-4 shrink-0" />
                            <span>{contact?.contactEmail}</span>
                          </div>
                          {phoneDisplay && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="h-4 w-4 shrink-0" />
                              <span>{phoneDisplay}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="offers" className="space-y-6 pt-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Offers
              </CardTitle>
              <Button
                size="sm"
                className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700"
                onClick={() => setAddOfferOpen(true)}
                disabled={!id || offerCategoryOptions.length === 0}
                title={
                  offerCategoryOptions.length === 0
                    ? "Add categories to this merchant before creating offers"
                    : undefined
                }
              >
                <Plus className="h-4 w-4" /> Add Offer
              </Button>
            </CardHeader>
            <CardContent className="p-6">
              {offerCategoryOptions.length === 0 && (
                <p className="text-sm text-center text-gray-500 max-w-md mx-auto mb-6">
                  This merchant has no categories yet. Categories from merchant
                  setup are required before you can add offers.
                </p>
              )}

              {offersLoading ? (
                <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  Loading offers…
                </div>
              ) : offers.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <p className="font-medium text-gray-700 mb-1">No offers found</p>
                  <p className="text-sm mb-4">
                    {offerCategoryOptions.length === 0
                      ? "Add categories to this merchant, then create an offer."
                      : "Create an offer for this merchant to see it listed here."}
                  </p>
                  {offerCategoryOptions.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-rose-200 text-rose-900 hover:bg-rose-50"
                      onClick={() => setAddOfferOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add your first offer
                    </Button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {offers.map((offer) => (
                    <Card
                      key={offer.id}
                      className="border border-gray-200 hover:shadow-md transition-shadow overflow-hidden"
                    >
                      <CardContent className="p-0">
                        <div className="w-full h-32 overflow-hidden bg-gray-100 flex items-center justify-center">
                          <img
                            src={offer.imageSrc}
                            alt={offer.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/placeholder.svg";
                            }}
                          />
                        </div>
                        <div className="p-4 space-y-3">
                          <div className="flex justify-between items-start gap-2">
                            <CardTitle className="text-base font-semibold text-gray-900 line-clamp-2 p-0">
                              {offer.title}
                            </CardTitle>
                            <Badge
                              variant="outline"
                              className={
                                offer.status === "Active"
                                  ? "shrink-0 bg-green-100 text-green-800 border-green-200"
                                  : "shrink-0 bg-gray-100 text-gray-700 border-gray-200"
                              }
                            >
                              {offer.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {offer.description || "—"}
                          </p>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Category</span>
                              <span className="font-medium text-right truncate">
                                {offer.category || "—"}
                              </span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">Start</span>
                              <span className="font-medium">
                                {formatOfferMillis(offer.startDateInMillis)}
                              </span>
                            </div>
                            <div className="flex justify-between gap-2">
                              <span className="text-gray-500">End</span>
                              <span className="font-medium">
                                {formatOfferMillis(offer.endDateInMillis)}
                              </span>
                            </div>
                          </div>
                          <div className="pt-1 text-sm">
                            <p className="text-gray-500 mb-0.5">Offer URL</p>
                            <p className="min-h-5 text-gray-800 break-all">
                              {offer.offerURL || ""}
                            </p>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex-1 border-rose-200 text-rose-800 hover:bg-rose-50"
                              aria-label={`Edit ${offer.title}`}
                              onClick={() => {
                                const categoryIdFallback =
                                  offer.categoryId ||
                                  (offerCategoryOptions[0]
                                    ? String(offerCategoryOptions[0].id)
                                    : "");
                                setEditOfferDraft({
                                  id: offer.id,
                                  title: offer.title,
                                  description: offer.description,
                                  startDateInMillis: offer.startDateInMillis,
                                  endDateInMillis: offer.endDateInMillis,
                                  offerURL: offer.offerURL,
                                  categoryId: categoryIdFallback,
                                  imageSrc: offer.imageSrc,
                                  termsAndConditions: offer.termsAndConditions,
                                  addressDetailsVOList:
                                    offer.addressDetailsVOList,
                                });
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                              aria-label={`Delete ${offer.title}`}
                              onClick={() => handleDeleteOfferClick(offer)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {id && (
            <AddOfferForOfferPageDialog
              open={addOfferOpen}
              onOpenChange={setAddOfferOpen}
              merchantId={id}
              categories={offerCategoryOptions}
              onSuccess={() => {
                fetchOffers();
              }}
            />
          )}

          <AlertDialog
            open={deleteOfferConfirmOpen}
            onOpenChange={(open) => {
              setDeleteOfferConfirmOpen(open);
              if (!open) setOfferToDelete(null);
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete offer</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete{" "}
                  <span className="font-medium text-gray-900">
                    {offerToDelete?.title || "this offer"}
                  </span>
                  ? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deletingOffer}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void confirmDeleteOffer();
                  }}
                  disabled={deletingOffer}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {deletingOffer ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    "Delete"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {editOfferDraft && id && (
            <EditOfferForOfferPageDialog
              key={editOfferDraft.id}
              open
              onOpenChange={(next) => {
                if (!next) setEditOfferDraft(null);
              }}
              merchantId={id}
              categories={offerCategoryOptions}
              initialOffer={editOfferDraft}
              onSuccess={(savedOfferUrl) => {
                const offerId = editOfferDraft?.id;
                if (offerId) {
                  setOffers((prev) =>
                    prev.map((o) =>
                      o.id === offerId
                        ? {
                            ...o,
                            offerURL: normalizeOfferUrlForDisplay(savedOfferUrl),
                          }
                        : o
                    )
                  );
                }
                setEditOfferDraft(null);
                fetchOffers();
              }}
            />
          )}
        </TabsContent>
      </Tabs>

      {id && (
        <EditOfferMerchantSheet
          merchant={displayMerchant}
          merchantId={id}
          open={editSheetOpen}
          onOpenChange={setEditSheetOpen}
          onUpdated={(patch) => {
            setMerchant((prev) => ({
              ...(prev ?? (routeMerchant as IMerchant)),
              ...patch,
            }));
          }}
        />
      )}
    </div>
  );
};

export default OfferMerchantDetails;
